import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import ContactTable from '../../components/ContactTable/ContactTable';
import MolstarViewer from '../../components/MolstarViewer/MolstarViewer';
import ContactMap from '../../components/ContactMap/ContactMap';
import BarChartContact from '../../components/BarChartContact/BarChartContact';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import GraphlqlQuery from '../../components/GraphlqlQuery/GraphlqlQuery';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import AAHydrophobicityChart from '../../components/AAHydrophobicityChart/AAHydrophobicityChart';
import AAFrequencyChart from '../../components/AAFrequencyChart/AAFrequencyChart';
import AAPairHeatmap from '../../components/AAPairHeatmap/AAPairHeatmap';
import AADistanceBoxplot from '../../components/AADistanceBoxplot/AADistanceBoxplot';
import ContactNetwork from '../../components/ContactNetwork/ContactNetwork';
import {
  getIdentifiers,
  getContactsByStructure,
  getContactsByResidue,
  buildContactsCsvUrl,
  CONTACT_DISPLAY_LIMIT,
} from '../../ApiFunctions.js';

const INTRA_TAB_INDEX = 2;

const IntraContactSection = ({ type, target, activeTabIndex, annotationMap, contactStructureData, contactStructureLoading }) => {

  const [visibleIntraDialog, setVisibleIntraDialog] = useState(false);

  // Dropdown (from getIdentifiers) + selected structure
  const [identifiers, setIdentifiers] = useState([]);
  const [structurePDBIdIntra, setStructurePDBIdIntra] = useState(undefined);

  // Data for the current view
  const [structureContacts, setStructureContacts] = useState([]); // selected structure's rows
  const [tableContacts, setTableContacts] = useState([]);         // what the table shows
  const [loading, setLoading] = useState(false);

  // "Too many to render" gates (show message + CSV instead)
  const [structureTooMany, setStructureTooMany] = useState(false);
  const [residueTooMany, setResidueTooMany] = useState(false);
  const [residueMode, setResidueMode] = useState(false);          // table showing a residue search
  const [residueQuery, setResidueQuery] = useState('');
  const [residueInput, setResidueInput] = useState('');

  const uniprotAccessionNumber = useMemo(
    () => (target ? target.split('_')[0].toUpperCase() : undefined),
    [target]
  );

  // Molstar / interaction states
  const [molstarState, setMolstarState] = useState({ positions: [], chain: undefined, database: 'PDB' });
  const [contactData, setContactData] = useState([]); // parsed data for ContactMap / charts
  const [interaction, setInteraction] = useState({ x: undefined, y: undefined, distance: undefined, rowType: undefined, details: undefined });
  const [residues, setResidues] = useState([]);
  const [selectedRows, setSelectedRows] = useState(null);

  // ---- Parse the selected structure's contacts for plots + Molstar ----------
  const processedStructure = useMemo(() => {
    const contactMapData = [];
    const allChains = [];
    const allPositions = [];

    for (let i = 0; i < structureContacts.length; i++) {
      const item = structureContacts[i];
      contactMapData.push({
        contactPosition: item.contactPosition,
        anPosition: item.anPosition ? parseInt(item.anPosition.split('_')[1], 10) : null,
        details: item.details,
        distance: parseFloat(item.distance),
      });
      if (item.details && !selectedRows) {
        const parts = item.details.split('|');
        for (let j = 0; j < parts.length; j++) {
          const part = parts[j];
          const firstSlash = part.indexOf('/');
          if (firstSlash > -1) {
            const secondSlash = part.indexOf('/', firstSlash + 1);
            if (secondSlash > -1) {
              allChains.push(part.substring(0, firstSlash).trim());
              allPositions.push(parseInt(part.substring(firstSlash + 1, secondSlash), 10));
            }
          }
        }
      }
    }
    return { contactMapData, allChains, allPositions };
  }, [structureContacts, selectedRows]);

  // ---- Load the structure list once the tab is opened -----------------------
  useEffect(() => {
    if (activeTabIndex !== INTRA_TAB_INDEX || contactStructureLoading || !target) return;
    const identifiers = contactStructureData || [];
    setIdentifiers(identifiers);
    setStructurePDBIdIntra(identifiers?.length > 0 ? identifiers[0] : undefined);
  }, [activeTabIndex, contactStructureLoading, contactStructureData, target]);

  // ---- Load the selected structure's contacts -------------------------------
  useEffect(() => {
    if (!structurePDBIdIntra || !target) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setResidueMode(false);
      setResidueTooMany(false);
      setResidueQuery('');

      const { contacts, tooMany } = await getContactsByStructure(target, structurePDBIdIntra);
      if (cancelled) return;

      setStructureTooMany(tooMany);
      setStructureContacts(tooMany ? [] : contacts);
      setTableContacts(tooMany ? [] : contacts);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [structurePDBIdIntra, target]);

  // Feed the plots from the parsed structure contacts
  useEffect(() => {
    setContactData(processedStructure.contactMapData);
    if (!selectedRows) {
      const db = structurePDBIdIntra && structurePDBIdIntra.includes('AF-') ? 'AF' : 'PDB';
      setMolstarState({ positions: processedStructure.allPositions, chain: processedStructure.allChains, database: db });
    }
  }, [processedStructure, structurePDBIdIntra, selectedRows]);

  // Structure shown in Molstar: in residue mode it follows the selected row;
  // otherwise it is the dropdown structure.
  const viewerIdentifier = (residueMode && selectedRows) ? selectedRows.identifier : structurePDBIdIntra;

  // ---- Row selection highlight ------------------------
  useEffect(() => {
    const { x, y, details } = interaction;
    if (x !== undefined && y !== undefined && details && viewerIdentifier) {
      try {
        const parts = details.split('|');
        const chains = [];
        const pos = [];
        const res = [];
        for (let i = 0; i < parts.length; i++) {
          const split = parts[i].trim().split('/');
          if (split.length >= 3) {
            chains.push(split[0]);
            pos.push(parseInt(split[1], 10));
            res.push(split[2]);
          }
        }
        const hyphenCount = (viewerIdentifier.match(/-/g) || []).length;
        let databaseStructure = viewerIdentifier.includes('AF-') ? 'AF' : 'PDB';
        if (hyphenCount >= 2) databaseStructure = 'CO';
        setResidues(res);
        setMolstarState({ positions: pos, chain: chains, database: databaseStructure });
      } catch (error) {
        console.error('Error parsing interaction:', error);
      }
    }
  }, [interaction, viewerIdentifier]);

  const handlePDBidIntra = useCallback((e) => {
    const selected = e.target.value;
    if (selected !== structurePDBIdIntra) {
      setResidueMode(false);
      setResidueTooMany(false);
      setResidueQuery('');
      setResidueInput('');
      setSelectedRows(null);
      setInteraction({ x: undefined, y: undefined, distance: undefined, rowType: undefined, details: undefined });
      setStructurePDBIdIntra(selected);
    }
  }, [structurePDBIdIntra]);

  // Row click: only reload/switch structure when NOT in residue mode. In residue
  // mode the table stays; the viewer follows the row via viewerIdentifier.
  const handleTableStructureChange = useCallback((id) => {
    if (!residueMode) setStructurePDBIdIntra(id);
  }, [residueMode]);

  const interactionReset = useCallback(() => {
    setInteraction({ x: undefined, y: undefined, distance: undefined, rowType: undefined, details: undefined });
    setSelectedRows(null);
    setContactData(processedStructure.contactMapData);
    const db = structurePDBIdIntra && structurePDBIdIntra.includes('AF-') ? 'AF' : 'PDB';
    setMolstarState({ positions: processedStructure.allPositions, chain: processedStructure.allChains, database: db });
  }, [processedStructure, structurePDBIdIntra]);

  // ---- Search by residue ----------------------------------------------------
  const handleResidueSearch = useCallback(async (anPosition) => {
    const value = (anPosition || '').trim();
    if (!value) return;
    setLoading(true);
    setResidueQuery(value);
    const { contacts, tooMany } = await getContactsByResidue(value);
    setResidueTooMany(tooMany);
    setResidueMode(true);
    setTableContacts(tooMany ? [] : contacts);
    if (!tooMany && contacts.length > 0) {
      const first = contacts[0];
      const firstAnPos = (typeof first.anPosition === 'string' && first.anPosition.includes('_'))
        ? parseInt(first.anPosition.split('_')[1], 10) : first.anPosition;
      // rowKey must match ContactTable's dataKey so the DataTable highlights it.
      setSelectedRows({ ...first, rowKey: `${first.anPosition}|${first.identifier}|${first.contactPosition}|${first.details}` });
      setInteraction({ x: firstAnPos, y: first.contactPosition, distance: first.distance !== undefined ? parseFloat(first.distance) : 0, rowType: 'main', details: first.details });
    } else {
      setSelectedRows(null);
      setInteraction({ x: undefined, y: undefined, distance: undefined, rowType: undefined, details: undefined });
    }
    setLoading(false);
  }, []);

  const handleResidueClear = useCallback(() => {
    setResidueMode(false);
    setResidueTooMany(false);
    setResidueQuery('');
    setResidueInput('');
    setSelectedRows(null);
    setInteraction({ x: undefined, y: undefined, distance: undefined, rowType: undefined, details: undefined });
    setTableContacts(structureContacts);
  }, [structureContacts]);

  // CSV links for the "too many" cases
  const structureCsvUrl = buildContactsCsvUrl({ target, identifier: structurePDBIdIntra });
  const residueCsvUrl = buildContactsCsvUrl({ target, anPosition: residueQuery });

  const activeCsvUrl = residueTooMany ? residueCsvUrl : structureCsvUrl;
  const isTooMany = residueTooMany || (structureTooMany && !residueMode);
  const showViewer = !loading && !isTooMany && !!viewerIdentifier &&
    ((residueMode && !!selectedRows) || (!residueMode && structureContacts.length > 0));
  const showPlots = !loading && !isTooMany && !residueMode && structureContacts.length > 0 && !!structurePDBIdIntra;

  return (
    <>
      <h2 className="display-6 fw-bold mb-5">
        Intra-protein contacts
        <sup>
          <Button icon="pi pi-info" onClick={() => setVisibleIntraDialog(true)} text size="small" className="btn-dialog" />
        </sup>
      </h2>

      <Dialog
        header="Intra-protein contacts"
        visible={visibleIntraDialog}
        style={{ width: '50vw' }}
        onHide={() => setVisibleIntraDialog(false)}
        position="top-right"
        draggable={false}
        resizable={false}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
      >
        <p className="m-0 mb-2 text-justify">This section presents a list of intra-protein contacts derived from experimental structures calculated in-house. The table includes the following information: the residue position in the protein sequence, the UniProt accession number, the gene symbol, the Identifier of the 3D structure, the contact position, the distance, and the atoms involved in the contact. We define residue-residue contacts as those in which the Cβ atoms are spatially closer than 8 Å (Cα for glycine), which is a commonly used distance cutoff.</p>
        <GraphlqlQuery type={type} target={target} tab={activeTabIndex} />
        <p className="m-0 mb-1 text-justify">For more information, visit <Link to="/about/" target="_blank"><b> about </b></Link> or <Link to="/api/" target="_blank"><b> API </b></Link> page.</p>
      </Dialog>

      {/* Structure selector — Sem sombra e com texto de alto contraste */}
      <div className="row mb-3">
        <div className="col-md-5 mb-2">
          <div className="bg-light rounded-3 p-3 border-start border-3 ">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <label htmlFor="structure" className="form-label fw-bold mb-0 d-flex align-items-center gap-2 text-dark">
                <i className="pi pi-box text-primary"></i>
                <span>3D Structure</span>
              </label>
              {identifiers.length > 0 && (
                <span className="badge bg-white text-dark border fw-normal px-2 py-1 rounded-pill">
                  {identifiers.length} available
                </span>
              )}
            </div>

            <p className="fs--2 text-dark mb-3">
              Select a structure to view its contacts, contact map and 3D viewer below.
            </p>

            <Dropdown
              inputId="structure"
              value={structurePDBIdIntra}
              options={identifiers}
              onChange={handlePDBidIntra}
              filter
              placeholder="Select a structure"
              style={{ width: '100%' }}
              className="structure-dropdown"
              loading={contactStructureLoading}
              disabled={loading || identifiers.length === 0}
            />

            {structurePDBIdIntra && !residueMode && (
              <div className="d-flex align-items-center justify-content-between mt-3 pt-2 border-top border-200 fs--2">
                <span className="fw-semibold text-dark">Showing structure:</span>
                <span className="fw-bold font-monospace bg-white px-2 py-1 rounded border text-primary">
                  {structurePDBIdIntra.toUpperCase()}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Search by residue — side by side with the structure selector */}
        <div className="col-md-5 mb-2">
          <div className="bg-light rounded-3 p-3 border-start border-3 ">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <label htmlFor="residueSearch" className="form-label fw-bold mb-0 d-flex align-items-center gap-2 text-dark">
                <i className="pi pi-search text-primary"></i>
                <span>Search by residue</span>
              </label>
            </div>

            <p className="fs--2 text-dark mb-3">
              Enter a residue (e.g. {uniprotAccessionNumber ? `${uniprotAccessionNumber}_290` : 'P17612_290'}) to see its contacts across all structures.
            </p>

            <div className="d-flex gap-2">
              <InputText
                id="residueSearch"
                value={residueInput}
                onChange={(e) => setResidueInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleResidueSearch(residueInput); }}
                placeholder="e.g. P17612_290"
                style={{ width: '100%' }}
                disabled={loading}
              />
              <Button
                icon="pi pi-search"
                onClick={() => handleResidueSearch(residueInput)}
                disabled={loading || !residueInput}
                tooltip="Search this residue across all structures"
              />
            </div>

            {residueMode && (
              <div className="d-flex align-items-center justify-content-between mt-3 pt-2 border-top border-200 fs--2">
                <span className="fw-semibold text-dark">Showing residue:</span>
                <span className="fw-bold font-monospace bg-white px-2 py-1 rounded border text-primary">
                  {residueQuery.toUpperCase()}
                </span>
                <Button label="Clear" icon="pi pi-times" text size="small" onClick={handleResidueClear} />
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="row mb-4 d-flex align-items-center justify-content-center py-4">
          <ProgressSpinner style={{ width: 50, height: 50 }} strokeWidth="8" animationDuration=".5s" />
        </div>
      ) : isTooMany ? (
        /* Card "Too Many Contacts" — Limpo, sem sombra e com excelente legibilidade */
        <div className="row mb-4">
          <div className="col-12">
            <div className="bg-light rounded-3 p-4 border-start border-4 border-warning">
              <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                <div className="d-flex align-items-center gap-3">
                  <div className="rounded-circle bg-white border p-3 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '48px', height: '48px' }}>
                    <i className="pi pi-exclamation-triangle text-warning fs-4"></i>
                  </div>
                  <div>
                    <h5 className="fw-bold mb-1 text-dark">High Density Contact Dataset</h5>
                    <p className="mb-0 fs--2 text-dark">
                      This selection has more than <strong>{CONTACT_DISPLAY_LIMIT.toLocaleString()}</strong> contacts — too many to render in table view. Download the full set as CSV instead.
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <a
                    href={activeCsvUrl}
                    className="p-button p-button-warning p-button-outlined d-inline-flex align-items-center gap-2 fw-bold text-decoration-none px-3 py-2"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <i className="pi pi-download"></i>
                    <span>Download CSV</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="row mb-4">
          <ContactTable
            contactData={tableContacts}
            setInteraction={setInteraction}
            annotationMap={annotationMap}
            setStructurePDBIdIntra={handleTableStructureChange}
            interactionReset={interactionReset}
            setResidues={setResidues}
            type="intra"
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
          />
        </div>
      )}

      {/* Structure viewer — also shows in residue mode, following the clicked row */}
      {showViewer && (
        <>
          <h2 className="display-6 fw-bold mb-4">Structure</h2>
          <div className="row mb-4">
            <div className="col-md-2 mb-2">
              <div className="bg-light rounded-3 p-3">
                <div className="d-flex align-items-center mb-2">
                  <div className="burble-contact me-2"></div>
                  <p className="fs--2 text-justify m-0 text-dark">Contact position</p>
                </div>
                <p className="fs--2 text-justify mb-0 text-dark">
                  Click on the viewer plugin icons to access more advanced features
                  from <Link to="https://molstar.org/viewer-docs/" target="_blank">Mol*</Link>.
                </p>
              </div>
            </div>
            <div className="col-md-10">
              <div className="viewer-box">
                <MolstarViewer
                  pdbId={viewerIdentifier}
                  tab="intra-contacts"
                  contactPairs={tableContacts}
                  chain={molstarState.chain}
                  positions={molstarState.positions}
                  database={molstarState.database}
                  distance={interaction.distance}
                  rowType={interaction.rowType}
                  residues={residues}
                  uniac={uniprotAccessionNumber}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Plots — structure mode only (need the full structure's contacts) */}
      {showPlots && (
        <>
          <h2 className="display-6 fw-bold mb-4">Summary  |  {structurePDBIdIntra.toUpperCase()} </h2>
          <div className="row mb-3">
            <div className="col-md-4"><ContactMap contactData={contactData} /></div>
            <div className="col-md-4"><BarChartContact contactData={contactData} /></div>
            <div className="col-md-4"><AAHydrophobicityChart contactData={contactData} /></div>
          </div>
          <div className="row mb-4">
            <div className="col-md-4"><AAFrequencyChart contactData={contactData} /></div>
            <div className="col-md-4"><AAPairHeatmap contactData={contactData} /></div>
            <div className="col-md-4"><AADistanceBoxplot contactData={contactData} /></div>
          </div>
          <div className="row">
            <ContactNetwork key="kd-bar-intra" contactData={contactData} structureId={structurePDBIdIntra} id="kd-bar-intra" />
          </div>
        </>
      )}
    </>
  );
};

export default IntraContactSection;